'use client';

import { useEffect, useRef } from 'react';

interface ClientCalendlyProps {
  url: string;
  color?: string;
  textColor?: string;
  openUrl?: 'new_tab' | 'same_tab';
}

export default function ClientCalendly({ url, color = '#0069ff', textColor = '#ffffff', openUrl = 'new_tab' }: ClientCalendlyProps) {
  const observerRef = useRef<MutationObserver | null>(null);
  const elementsToCleanup = useRef<HTMLLinkElement | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if Calendly is already loaded
    if (window.Calendly) {
      initCalendly();
      return;
    }

    // Insert Calendly CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    elementsToCleanup.current = link;

    // Insert Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => initCalendly();
    document.body.appendChild(script);
    scriptRef.current = script;

    // Initialize Calendly when script loads
    function initCalendly() {
      if (typeof window.Calendly === 'undefined') return;

      try {
        window.Calendly.initBadgeWidget({
          url,
          color,
          textColor,
          openUrl
        });

        // Set up mutation observer for tracking clicks
        setupMutationObserver();
      } catch (error) {
        console.error('Error initializing Calendly:', error);
      }
    }

    // Set up mutation observer to track clicks
    function setupMutationObserver() {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      const targetNode = document.body;
      const config = { childList: true, subtree: true };
      
      observerRef.current = new MutationObserver((mutations) => {
        const calendlyElement = document.querySelector('.calendly-widget');
        if (calendlyElement) {
          const buttons = calendlyElement.querySelectorAll('button');
          buttons.forEach(button => {
            button.addEventListener('click', handleCalendlyClick);
          });
        }
      });

      observerRef.current.observe(targetNode, config);
    }

    function handleCalendlyClick() {
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
    }

    // Cleanup function
    return () => {
      // Clean up observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Clean up script
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.onload = null;
        scriptRef.current.remove();
      }

      // Clean up stylesheet
      if (elementsToCleanup.current && elementsToCleanup.current.parentNode) {
        elementsToCleanup.current.remove();
      }

      // Clean up Calendly if it exists
      if (typeof window.Calendly !== 'undefined') {
        try {
          window.Calendly.closePopupWidget();
        } catch (e) {
          console.warn('Error cleaning up Calendly:', e);
        }
      }
    };
  }, [url, color, textColor, openUrl]);

  return <div className="calendly-badge" />;
}
